import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Row, Col, Card, ListGroup, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import ApiService from '../../services/ApiService';
import PaymentCardTransaction from './PaymentCardTransaction';
import { PaymentSession, PaymentSessionSuccessResponse } from './types';
import CardPayment from './CardPayment';
import { modal } from '../modal/ModalEmitter';
import FlexAlert from '../FlexAlert';

interface Props {
  amount: number;
  invoiceNum: string;
  onSuccess: (response: PaymentSessionSuccessResponse) => void;
  userInitials: string;
}

const PaymentSessionRefundSimple = ({ amount, invoiceNum, onSuccess, userInitials: userInitialsProp = '' }: Props) => {
  const user = useSelector((state: any) => state.app.user);
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>();
  const [cardPayments, setCardPayments] = useState<CardPayment[]>([]);
  const [refunding, setRefunding] = useState(false);
  const [userInitials] = useState(userInitialsProp || user.ppc || '');

  useEffect(() => {
    ApiService.get('/payment/session', {
      params: {
        invoiceNum,
      },
    }).then((response: AxiosResponse) => {
      const sessions = response.data;

      if (sessions && sessions.length) {
        const session: PaymentSession = sessions[0];
        const payments = [];
        for (const tx of session.CardTransactions) {
          payments.push(new CardPayment(tx));
        }
        setCardPayments(payments);
        setPaymentSession(session);
      } else {
        setPaymentSession(null);
      }
    });
  }, []);

  if (paymentSession === null) {
    return (
      <FlexAlert modalClose>
        <h5>
          Credit card payment not found for <b className="text-warning">{invoiceNum}</b>
        </h5>
      </FlexAlert>
    );
  }

  if (!paymentSession) {
    // loading
    return null;
  }

  const handleRefund = () => {
    modal.confirm({
      title: 'Refund',
      message: 'This will refund all charges!',
      ok: async () => {
        setRefunding(true);
        modal.close(); // close confirm

        const entries = [];

        try {
          for (const payment of cardPayments) {
            if (payment.transactionType === 'sale' && !payment.isVoid() && !payment.isFullyRefunded()) {
              const { firstName, lastName } = payment.getName();

              entries.push({
                address: '',
                amount: -1 * payment.getNetAmount(),
                cardBin: payment.cardBin,
                cardEntry: payment.cardEntry,
                cardExp: payment.cardExp,
                cardLast4: payment.cardLast4,
                cardType: payment.cardType,
                firstName,
                gateway: payment.gateway,
                hash: '',
                lastName,
                signature: '',
                terminalName: payment.terminalName,
                token: payment.cardToken,
                transactionId: payment.id,
                zip: '',
                transactionResult: payment.transactionResult,
              });
              await payment.doVoidOrRefundPayment({ userInitials, sessionId: paymentSession.id });
            }
          }

          modal.close(); // close self

          onSuccess({
            transactions: entries,
            cash: 0,
            sessionId: paymentSession.id,
            invoices: [{ invoiceNum, amount }],
            customerNum: paymentSession.customerNum,
            branchName: paymentSession.branchName,
            mvAccount: paymentSession.mvAccount,
          });
        } catch (err) {
          console.error(err);
        }

        setRefunding(false);
      },
    });
  };

  const renderTransactions = () => {
    return cardPayments
      .filter((tx: CardPayment) => tx.transactionType === 'sale')
      .map((tx: CardPayment, i: number) => (
        <ListGroup.Item key={i}>
          <PaymentCardTransaction paymentTransaction={tx} userInitials={userInitials} readOnly />
        </ListGroup.Item>
      ));
  };

  return (
    <>
      <Row className="flex-grow-1">
        <Col md={12} className="p-3">
          <Card style={{ fontSize: '19px', backgroundColor: '#eee', borderRadius: '3px' }} className="mb-3">
            <Card.Body>
              Refunding <span className="text-success text-bold">${amount.toFixed(2)}</span> for invoice {invoiceNum}
            </Card.Body>
          </Card>
          <div>
            <ListGroup className="mb-3">{renderTransactions()}</ListGroup>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>
      <Row>
        <Col className="text-right">
          <Button onClick={handleRefund} disabled={refunding}>
            Refund
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default PaymentSessionRefundSimple;
